import {
	type CurrencyEnum,
	type GetProductSamplePricesQuery,
	useDeleteProductSamplePriceMutation,
	useGetProductSamplePricesQuery,
	useSetProductSamplePriceMutation,
} from '@codezo/graphql';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { CurrencyInput } from '../../components/ui/currency-input';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { NumberInput } from '../../components/ui/number-input';
import { SectionCard } from '../../components/ui/section-card';
import { VetterSelector } from '../../components/selectors/VetterSelector';
import { formatDate } from '../../lib/utils';
import { useCurrencyStore } from '../../stores/currency';
import { money } from './PriceListBlock';
import { formatDayRange, formatLeadTime } from '../../components/pricing/SampleCatalogPicker';

type SampleRow = GetProductSamplePricesQuery['getProductSamplePrices'][number];

/** The VetterSelector value standing for the house price, which has no supplier id. */
const HOUSE = 'house';

/** "8–10 days", for the hover text. */
const days = (min?: number | null, max?: number | null) => {
	const range = formatDayRange(min, max);
	return range ? `${range} days` : null;
};

/* Every column shares the width, the supplier's only a little more than the
   rest. One flexible column beside fixed ones took all the slack and pushed the
   figures to the far edge. */
const COLUMNS =
	'grid-cols-[minmax(150px,1.4fr)_minmax(100px,1fr)_minmax(90px,1fr)_minmax(100px,1fr)_minmax(90px,1fr)_56px]';

/**
 * The catalogue sample prices for one product: the default price, and one per
 * supplier.
 *
 * Unlike the cost ladder above it, these are what the CLIENT pays — an offer
 * sample has one entered price plus freight, no cost and no markup — and there
 * is no quantity: a supplier has at most one sample price per product. Shown in
 * the header currency, converted by the backend.
 */
export function ProductSamplePricesSection({ productId }: { productId: string }) {
	const { selectedCurrency } = useCurrencyStore();
	const [includeInactive, setIncludeInactive] = useState(false);
	const [editing, setEditing] = useState<{ row: SampleRow | null } | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<SampleRow | null>(null);

	const { data, loading, refetch } = useGetProductSamplePricesQuery({
		variables: { input: { productId, includeInactive }, currency: selectedCurrency },
		fetchPolicy: 'no-cache',
	});
	const rows = data?.getProductSamplePrices ?? [];

	const [deletePrice, { loading: deleting }] = useDeleteProductSamplePriceMutation();

	const handleDelete = async () => {
		if (!deleteTarget) return;
		try {
			await deletePrice({ variables: { input: { id: deleteTarget.id } } });
			toast.success('Sample price removed');
			setDeleteTarget(null);
			void refetch();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to remove sample price');
		}
	};

	const storedIn = [...new Set(rows.map((r) => r.currency))];
	const currencyNote = !storedIn.some((c) => c !== selectedCurrency)
		? null
		: storedIn.length === 1
			? `Shown in ${selectedCurrency} · stored in ${storedIn[0]}`
			: `Shown in ${selectedCurrency} · hover a price to see what is stored`;

	const nameOf = (row: SampleRow) => row.vetterEntity?.name ?? 'Default price';

	return (
		<>
			<SectionCard
				title="Sample prices"
				action={
					<button onClick={() => setEditing({ row: null })} title="Add a sample price">
						<Plus size={12} />
						Add sample price
					</button>
				}
			>
				<div className="flex flex-wrap items-center justify-between gap-3 border-b border-line-1 px-4 py-3">
					{/* Said once for the card: what these are, and how a supplier's own
					    price relates to the default one. */}
					<span className="text-xs text-text-3">
						What the client pays. A supplier&apos;s own price is used before the
						default.
						{currencyNote && ` ${currencyNote}.`}
					</span>
					<label className="flex cursor-pointer items-center gap-2">
						<Checkbox
							checked={includeInactive}
							onCheckedChange={(v) => setIncludeInactive(v === true)}
						/>
						<span className="text-sm text-text-3">Show inactive</span>
					</label>
				</div>

				{loading ? (
					<div className="space-y-2 p-4">
						{[0, 1].map((i) => (
							<div key={i} className="h-8 animate-pulse rounded bg-surface-fill" />
						))}
					</div>
				) : rows.length === 0 ? (
					<div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
						<div className="text-sm text-text-3">
							No sample prices for this product yet.
						</div>
						<Button
							variant="outline"
							size="sm"
							onClick={() => setEditing({ row: null })}
						>
							<Plus size={14} data-icon="inline-start" />
							Add sample price
						</Button>
					</div>
				) : (
					<div className="overflow-x-auto">
						<div className="min-w-[640px]">
							<div
								className={`grid ${COLUMNS} gap-2 px-4 py-1.5 text-2xs font-medium uppercase tracking-[0.05em] text-text-4`}
							>
								<span>Supplier</span>
								<span>Price</span>
								<span>Shipping</span>
								<span>Lead time</span>
								<span>Last quoted</span>
								<span />
							</div>
							{rows.map((row) => {
								/* Header-currency figures; null only without a rate, and
								   then the stored figures are shown as they are. */
								const fig = row.converted;
								const shown = fig?.currency ?? row.currency;
								const stored =
									fig && fig.currency !== row.currency
										? `Stored as ${money(row.samplePrice, row.currency)}${
												row.shippingPrice != null
													? ` + ${money(row.shippingPrice, row.currency)} shipping`
													: ''
											}`
										: undefined;
								const shipping = fig ? fig.shippingPrice : row.shippingPrice;
								return (
									<div
										key={row.id}
										className={`grid ${COLUMNS} min-h-10 items-center gap-2 border-t border-line-1 px-4 text-sm`}
									>
										<span className="flex min-w-0 items-center gap-1.5">
											<span
												className="truncate font-medium text-text-1"
												title={nameOf(row)}
											>
												{nameOf(row)}
											</span>
											{!row.isActive && (
												<span className="shrink-0 rounded-full border border-line-2 px-1.5 py-px text-2xs text-text-3">
													Inactive
												</span>
											)}
										</span>
										<span
											className="font-mono font-medium text-text-1"
											title={stored}
										>
											{money(fig?.samplePrice ?? row.samplePrice, shown)}
										</span>
										<span className="font-mono text-text-2" title={stored}>
											{shipping != null ? money(shipping, shown) : '—'}
										</span>
										<span
											className="whitespace-nowrap text-text-2"
											title={`Production ${days(row.minProductionDays, row.maxProductionDays) ?? 'not set'} · delivery ${days(row.deliveryDaysMin, row.deliveryDaysMax) ?? 'not set'}`}
										>
											{formatLeadTime(row) ?? '—'}
										</span>
										<span className="whitespace-nowrap text-xs text-text-3">
											{row.lastQuotedAt ? formatDate(row.lastQuotedAt) : '—'}
										</span>
										<span className="flex items-center justify-end gap-0.5">
											<button
												type="button"
												onClick={() => setEditing({ row })}
												aria-label="Edit sample price"
												className="rounded p-1 text-text-4 transition-colors hover:text-text-1"
											>
												<Pencil size={14} />
											</button>
											<button
												type="button"
												onClick={() => setDeleteTarget(row)}
												aria-label="Remove sample price"
												className="rounded p-1 text-text-4 transition-colors hover:text-destructive"
											>
												<Trash2 size={14} />
											</button>
										</span>
									</div>
								);
							})}
						</div>
					</div>
				)}
			</SectionCard>

			{editing && (
				<SetProductSamplePriceModal
					open
					onOpenChange={(o) => !o && setEditing(null)}
					productId={productId}
					row={editing.row}
					onSuccess={() => void refetch()}
				/>
			)}

			<ConfirmDialog
				open={!!deleteTarget}
				onOpenChange={(o) => !o && setDeleteTarget(null)}
				title="Remove this sample price?"
				description={
					deleteTarget
						? `The sample price for ${nameOf(deleteTarget)} will be deleted.`
						: ''
				}
				confirmLabel="Remove"
				loading={deleting}
				onConfirm={handleDelete}
			/>
		</>
	);
}

/** A form value from a figure: empty for null, the exact figure otherwise. */
const field = (value?: number | null) => (value != null ? String(value) : '');
const intOrNull = (value: string) => (value.trim() === '' ? null : parseInt(value, 10));

/**
 * Adds or edits one sample price.
 *
 * Amounts are shown and sent in the header currency. An amount sent back
 * exactly as it was shown keeps the stored figure and currency on the backend,
 * so the figures are prefilled with the backend's converted values untouched —
 * reformatting them here would read as an edit and re-author the price.
 */
function SetProductSamplePriceModal({
	open,
	onOpenChange,
	productId,
	row,
	onSuccess,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	productId: string;
	row: SampleRow | null;
	onSuccess: () => void;
}) {
	const { selectedCurrency } = useCurrencyStore();
	const [supplier, setSupplier] = useState<string>(HOUSE);
	const [samplePrice, setSamplePrice] = useState('');
	const [shippingPrice, setShippingPrice] = useState('');
	const [minProductionDays, setMinProductionDays] = useState('');
	const [maxProductionDays, setMaxProductionDays] = useState('');
	const [deliveryDaysMin, setDeliveryDaysMin] = useState('');
	const [deliveryDaysMax, setDeliveryDaysMax] = useState('');
	const [isActive, setIsActive] = useState(true);
	const [setPrice, { loading }] = useSetProductSamplePriceMutation();

	/* Without a rate the backend could not convert, so the row is edited in
	   the currency it is stored in rather than relabelled as the header's. */
	const currency = (row && !row.converted ? row.currency : selectedCurrency) as CurrencyEnum;

	/* EVERY row, inactive and expired included: saving overwrites whatever row
	   this supplier already has, switched off or not, so the "replaces it"
	   warning has to see those too — the list behind the modal may hide them. */
	const { data: everyRowData } = useGetProductSamplePricesQuery({
		variables: { input: { productId, includeInactive: true }, currency: selectedCurrency },
		skip: !open || !!row,
		fetchPolicy: 'no-cache',
	});
	const everyRow = everyRowData?.getProductSamplePrices ?? [];

	useEffect(() => {
		if (!open) return;
		setSupplier(row?.vetterEntityId ?? HOUSE);
		setSamplePrice(field(row ? (row.converted?.samplePrice ?? row.samplePrice) : null));
		setShippingPrice(
			field(row ? (row.converted ? row.converted.shippingPrice : row.shippingPrice) : null)
		);
		setMinProductionDays(field(row?.minProductionDays));
		setMaxProductionDays(field(row?.maxProductionDays));
		setDeliveryDaysMin(field(row?.deliveryDaysMin));
		setDeliveryDaysMax(field(row?.deliveryDaysMax));
		setIsActive(row?.isActive ?? true);
	}, [open, row]);

	const vetterEntityId = supplier === HOUSE ? null : supplier;
	/* A supplier has one sample price, so adding one where it already has a
	   price replaces it. Said before saving rather than discovered after. */
	const replaces = !row && everyRow.some((r) => (r.vetterEntityId ?? null) === vetterEntityId);

	const handleSubmit = async () => {
		const sample = parseFloat(samplePrice);
		if (!Number.isFinite(sample) || sample < 0) return;
		const shipping = shippingPrice.trim() === '' ? null : parseFloat(shippingPrice);
		try {
			await setPrice({
				variables: {
					input: {
						productId,
						vetterEntityId,
						samplePrice: sample,
						shippingPrice: shipping,
						currency,
						minProductionDays: intOrNull(minProductionDays),
						maxProductionDays: intOrNull(maxProductionDays),
						deliveryDaysMin: intOrNull(deliveryDaysMin),
						deliveryDaysMax: intOrNull(deliveryDaysMax),
						// Validity is not edited here; send what the row has so saving
						// does not clear it.
						validFrom: row?.validFrom ?? null,
						validTo: row?.validTo ?? null,
						isActive,
					},
				},
			});
			toast.success(row ? 'Sample price updated' : 'Sample price added');
			onOpenChange(false);
			onSuccess();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to save sample price');
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{row ? 'Edit sample price' : 'Add sample price'}</DialogTitle>
				</DialogHeader>
				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-3">
							<div className="flex flex-col gap-1">
								<Label>Supplier</Label>
								{/* Fixed when editing: a row IS one supplier's price, and moving
								    it would silently replace another's. */}
								{row ? (
									<span className="text-sm text-text-1">
										{row.vetterEntity?.name ?? 'Default price'}
									</span>
								) : (
									<VetterSelector
										value={supplier}
										onChange={(v) => setSupplier(v || HOUSE)}
										extraOptions={[{ value: HOUSE, label: 'Default price' }]}
										selectedLabel={
											supplier === HOUSE ? 'Default price' : undefined
										}
									/>
								)}
								{replaces && (
									<span className="text-xs text-status-warn">
										This supplier already has a sample price. Saving replaces
										it.
									</span>
								)}
							</div>

							<div className="grid grid-cols-2 gap-2">
								<div className="flex flex-col gap-1">
									<Label>
										Sample price <span className="text-destructive">*</span>
									</Label>
									<CurrencyInput
										currency={currency}
										value={samplePrice}
										onChange={(e) =>
											setSamplePrice((e.target as HTMLInputElement).value)
										}
										placeholder="0.00"
										min={0}
										step="any"
									/>
								</div>
								<div className="flex flex-col gap-1">
									<Label>Shipping price</Label>
									<CurrencyInput
										currency={currency}
										value={shippingPrice}
										onChange={(e) =>
											setShippingPrice((e.target as HTMLInputElement).value)
										}
										placeholder="Not known"
										min={0}
										step="any"
									/>
								</div>
							</div>

							<div className="grid grid-cols-2 gap-2">
								<div className="flex flex-col gap-1">
									<Label>Min production days</Label>
									<NumberInput
										value={minProductionDays}
										onChange={(e) =>
											setMinProductionDays(
												(e.target as HTMLInputElement).value
											)
										}
										placeholder="—"
										min={0}
									/>
								</div>
								<div className="flex flex-col gap-1">
									<Label>Max production days</Label>
									<NumberInput
										value={maxProductionDays}
										onChange={(e) =>
											setMaxProductionDays(
												(e.target as HTMLInputElement).value
											)
										}
										placeholder="—"
										min={0}
									/>
								</div>
								<div className="flex flex-col gap-1">
									<Label>Min delivery days</Label>
									<NumberInput
										value={deliveryDaysMin}
										onChange={(e) =>
											setDeliveryDaysMin((e.target as HTMLInputElement).value)
										}
										placeholder="—"
										min={0}
									/>
								</div>
								<div className="flex flex-col gap-1">
									<Label>Max delivery days</Label>
									<NumberInput
										value={deliveryDaysMax}
										onChange={(e) =>
											setDeliveryDaysMax((e.target as HTMLInputElement).value)
										}
										placeholder="—"
										min={0}
									/>
								</div>
							</div>

							<label className="flex cursor-pointer items-center gap-2">
								<Checkbox
									checked={isActive}
									onCheckedChange={(v) => setIsActive(v === true)}
								/>
								<span className="text-sm text-text-2">
									Active — used to prefill new samples
								</span>
							</label>
						</div>
					</DialogBody>
					<DialogFooter>
						<Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
							Cancel
						</Button>
						<Button type="submit" disabled={samplePrice.trim() === '' || loading}>
							{loading ? 'Saving...' : row ? 'Update' : replaces ? 'Replace' : 'Add'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
